<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'PrinterConfig',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    oid: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const IPPVersions = ref(['1.0', '1.1', '2.0', '2.1', '2.2']);
  const formId = ref(null);
  const formName = ref('PrinterConfig');
  const config = ref({
    name: {
      label: $fn.value.pgettext('PrinterConfig', 'Printer Name'),
      help: $fn.value.pgettext(
        'PrinterConfig',
        'A friendly name for the printer to help identify it easily.'
      ),
    },
    uri: {
      label: $fn.value.pgettext('PrinterConfig', 'Printer URI'),
      help: 'P_("PrinterConfig", "The network address of the printer (e.g., ipp://printer.local:631/printers/Office). Must be a valid URL.")',
      required: true,
    },
    proxy: {
      label: $fn.value.pgettext('PrinterConfig', 'Proxy'),
      help: $fn.value.pgettext(
        'PrinterConfig',
        'The URL of the proxy server, if any.'
      ),
    },
    ipp_user_name: {
      label: $fn.value.pgettext('PrinterConfig', 'IPP User Name'),
      help: $fn.value.pgettext(
        'PrinterConfig',
        'The username used for IPP (Internet Printing Protocol) authentication.'
      ),
      required: true,
    },
    ipp_version: {
      label: $fn.value.pgettext('PrinterConfig', 'IPP Version'),
      help: $fn.value.pgettext(
        'PrinterConfig',
        'Specifies the IPP protocol version used for communication (default is 2.0).'
      ),
      required: true,
      default: '2.0',
    },
    is_default: {
      label: $fn.value.pgettext('PrinterConfig', 'Set as Default Printer'),
      help: $fn.value.pgettext(
        'PrinterConfig',
        'Mark this printer as the default option for print jobs.'
      ),
    },
  });
  const resource = ref(null);
  const editor = ref(null);
  const selectedFile = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    IPPVersions: IPPVersions.value,
    formId: formId.value,
    formName: formName.value,
    config: config.value,
    resource: resource.value,
    editor: editor.value,
    selectedFile: selectedFile.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_resource = async () => {
    return props.oid
      ? $fn.value.fetch('get_object_by_type', {
          parameters: { id: props.oid, object_type: 'printer' },
          asResource: true,
        })
      : new $fn.value.Resource(
          _.reduce($data.value.config, (m, v, k) =>
            _.set(m, k, _.get(v, 'default', null))
          )
        );
  };
  const $$load_editor = async () => {
    return resource.value.editor();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'IPPVersions' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'config' },
      { name: 'resource' },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'selectedFile' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      formId: $$load_formId,
      resource: $$load_resource,
      editor: $$load_editor,
    },
    ux: {},
    dataRefs: {
      IPPVersions,
      formId,
      formName,
      config,
      resource,
      editor,
      selectedFile,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          $fn.value.pgettext('PrinterConfig|Title', 'Printer configuration'),
        ],
        icon: $config.value.settings.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_5kqwae7c9_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_2vba2384a_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_p7vwcx6hc_getActionArgs = async ($event) => {
    return {
      objectType: 'object_by_type',
      op: 'upsert',
      data: { ...editor.value.getData(), object_type: 'printer' },
    };
  };
  const act_2140198529698299_closeModal_hucl2jqf8_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2140198529698299_request_mvmxi7956_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_object_by_type',
      data: resource.value.getData({ fields: ['id', 'etag'] }),
      parameters: {
        object_type: 'printer',
      },
    };
  };
  const act_2167826738365259_closeModal_7wtbpculf_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
    };
  };

  const act_7315092382398562_closeModal_5kqwae7c9 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_5kqwae7c9_getActionArgs,
    });
  const inst_evh_7315092382398562_close_xqpog7n0n =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_5kqwae7c9,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_xqpog7n0n],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_tr1qa8d9c =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_gth9e9pxw =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_tr1qa8d9c,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_gth9e9pxw],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_mcp04bux6 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_6gd0r9nka =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_mcp04bux6,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_6gd0r9nka],
    event: 'close',
  });
  const act_1040167445267876_closeModal_2vba2384a =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_2vba2384a_getActionArgs,
    });
  const inst_evh_1040167445267876_success_kny2bzb1t =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_2vba2384a,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_p7vwcx6hc = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_p7vwcx6hc_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_kny2bzb1t],
  });
  const inst_evh_1040167445267876_submit_aton0ghsc =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_p7vwcx6hc,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_aton0ghsc],
    event: 'submit',
  });
  const act_2140198529698299_closeModal_hucl2jqf8 =
    new actionTypes.CloseModalAction({
      actionArgs: act_2140198529698299_closeModal_hucl2jqf8_getActionArgs,
    });
  const inst_evh_2140198529698299_success_h9jrobh8q =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeModal_hucl2jqf8,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_2140198529698299_request_mvmxi7956 = new actionTypes.RequestAction({
    actionArgs: act_2140198529698299_request_mvmxi7956_getActionArgs,
    displayName: 'delete',
    events: [inst_evh_2140198529698299_success_h9jrobh8q],
  });
  const inst_evh_2140198529698299_clickPreventStop_f15xmci6g =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_request_mvmxi7956,
      event: 'click.prevent.stop',
      displayName: 'delete',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_f15xmci6g],
      event: 'click.prevent.stop',
    });
  const act_2167826738365259_closeModal_7wtbpculf =
    new actionTypes.CloseModalAction({
      actionArgs: act_2167826738365259_closeModal_7wtbpculf_getActionArgs,
    });
  const inst_evh_2167826738365259_clickPreventStop_onowhxo0q =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_closeModal_7wtbpculf,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_onowhxo0q],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_xqpog7n0n,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_gth9e9pxw,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_6gd0r9nka,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_kny2bzb1t,
    inst_evh_1040167445267876_submit_aton0ghsc,
    save: inst_evh_1040167445267876_submit_aton0ghsc,
    inst_evh_1040167445267876_submit,
    inst_evh_2140198529698299_success_h9jrobh8q,
    inst_evh_2140198529698299_clickPreventStop_f15xmci6g,
    delete: inst_evh_2140198529698299_clickPreventStop_f15xmci6g,
    inst_evh_2140198529698299_clickPreventStop,
    inst_evh_2167826738365259_clickPreventStop_onowhxo0q,
    inst_evh_2167826738365259_clickPreventStop,
  };

  const evh_7315092382398562_close_xqpog7n0n = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_xqpog7n0n.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_gth9e9pxw = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_gth9e9pxw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_6gd0r9nka = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_6gd0r9nka.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_kny2bzb1t = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_kny2bzb1t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_aton0ghsc = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_aton0ghsc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success_h9jrobh8q = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success_h9jrobh8q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_f15xmci6g = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_f15xmci6g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_onowhxo0q = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_onowhxo0q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="PrinterConfig"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        :readonly="resource.locked"
        :labelAlign="$config.forms.defaultLabelAlign"
        :id="formId"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpRow>
          <ItpCol>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="uri"
              :label="config.uri.label"
              :helpTooltip="config.uri.help"
              :required="config.uri.required"
            >
              <ItpFormInput
                name="uri"
                v-model="editor.uri"
                :value="editor.uri"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.uri =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                :required="config.uri.required"
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="proxy"
              :label="config.proxy.label"
              :helpTooltip="config.proxy.help"
              :required="config.proxy.required"
            >
              <ItpFormInput
                name="proxy"
                v-model="editor.proxy"
                :value="editor.proxy"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.proxy =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                :required="config.proxy.required"
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="name"
              :label="config.name.label"
              :helpTooltip="config.name.help"
              :required="config.name.required"
            >
              <ItpFormInput
                name="name"
                v-model="editor.name"
                :value="editor.name"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.name =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                :required="config.name.required"
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="ipp_user_name"
              :label="config.ipp_user_name.label"
              :helpTooltip="config.ipp_user_name.help"
              :required="config.ipp_user_name.required"
            >
              <ItpFormInput
                name="ipp_user_name"
                v-model="editor.ipp_user_name"
                :value="editor.ipp_user_name"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.ipp_user_name =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                :required="config.ipp_user_name.required"
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="2"
              name="ipp_version"
              :label="config.ipp_version.label"
              :helpTooltip="config.ipp_version.help"
              :required="config.ipp_version.required"
            >
              <ItpFormSelect
                name="ipp_version"
                v-model="editor.ipp_version"
                :value="editor.ipp_version"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.ipp_version =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                :options="IPPVersions"
                :required="config.ipp_version.required"
              >
              </ItpFormSelect>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="is_default"
              :label="config.is_default.label"
              :helpTooltip="config.is_default.help"
            >
              <ItpFormCheckbox
                name="is_default"
                v-model="editor.is_default"
                :value="editor.is_default"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.is_default =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormCheckbox>
            </ItpFormGroup>
          </ItpCol>
        </ItpRow>
      </ItpForm>
      <hr v-if="!resource.isNew" />
      <PrinterTester :printerId="resource.id" v-if="!resource.isNew">
      </PrinterTester>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Delete')"
          variant="danger"
          icon="fa-times"
          :spinning="actions.delete.isRunning"
          class="me-auto"
          v-if="!resource.isNew"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :form="forms[formName] && forms[formName].id"
          :text="pgettext('Button', 'Save')"
          type="submit"
          variant="primary"
          default
          :disabled="!forms[formName] || !forms[formName].submittable"
          :spinning="actions.submit && actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Button', 'Close')"
          variant="default"
          :disabled="actions.submit && actions.submit.isRunning"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
